import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Package, ChevronRight, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function MobileWarehouseReceivePage() {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ truck_id: '', po_number: '', completed_by: '' });
  const [creating, setCreating] = useState(false);

  const fetchReceipts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/warehouse/receipts`);
      setReceipts(res.data || []);
    } catch (err) {
      console.error('Failed to fetch receipts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const handleCreate = async () => {
    if (!createForm.po_number.trim()) {
      toast.error('PO number is required');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/warehouse/receipts`, createForm);
      toast.success('Receipt created');
      setShowCreate(false);
      setCreateForm({ truck_id: '', po_number: '', completed_by: '' });
      // Navigate to the receipt detail
      navigate(`/warehouse/receipts/${res.data.receipt_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create receipt');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="mobile-page">
        <div className="mobile-loading"><div className="mobile-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="mobile-page">
      <button className="mobile-back-btn" onClick={() => navigate('/mobile/warehouse')}>
        <ArrowLeft size={18} /> Warehouse
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="mobile-page-title">Receive</div>
        <button className="mobile-action-btn primary" style={{ width: 'auto', padding: '10px 16px', fontSize: 14 }} onClick={() => setShowCreate(true)}>
          <Plus size={16} /> New Receipt
        </button>
      </div>

      {/* Receipts List */}
      {receipts.length === 0 ? (
        <div className="mobile-empty-state">
          <Package />
          <p>No receipts found</p>
        </div>
      ) : (
        receipts.map((r) => (
          <div
            key={r.receipt_id}
            className="mobile-wo-card"
            style={{ borderLeftColor: r.status === 'completed' ? '#34c759' : '#ff9500', cursor: 'pointer' }}
            onClick={() => navigate(`/warehouse/receipts/${r.receipt_id}`)}
          >
            <div className="mobile-wo-header">
              <div>
                <div className="mobile-wo-number">Receipt {r.receipt_id?.slice(0, 8)}</div>
                <div className="mobile-wo-item-name">PO: {r.po_number || 'N/A'}</div>
              </div>
              <span className={`mobile-status-badge ${r.status === 'completed' ? 'completed' : 'staged'}`}>
                {r.status || 'In Progress'}
              </span>
            </div>
            <div className="mobile-wo-meta">
              {r.truck_id && (
                <span className="mobile-wo-meta-item">
                  <Package size={14} /> Truck: {r.truck_id}
                </span>
              )}
              <span className="mobile-wo-meta-item">
                <Clock size={14} /> {r.line_items?.length || 0} items
              </span>
            </div>
          </div>
        ))
      )}

      {/* Create Receipt Sheet */}
      {showCreate && (
        <div className="mobile-sheet-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div className="mobile-sheet">
            <div className="mobile-sheet-handle" />
            <div className="mobile-sheet-title">New Receipt</div>
            <div className="mobile-input-group">
              <label className="mobile-input-label">PO Number *</label>
              <input className="mobile-input" placeholder="PO-XXXXX" value={createForm.po_number} onChange={e => setCreateForm(f => ({ ...f, po_number: e.target.value }))} />
            </div>
            <div className="mobile-input-group">
              <label className="mobile-input-label">Truck ID</label>
              <input className="mobile-input" placeholder="Truck ID (optional)" value={createForm.truck_id} onChange={e => setCreateForm(f => ({ ...f, truck_id: e.target.value }))} />
            </div>
            <div className="mobile-input-group">
              <label className="mobile-input-label">Completed By</label>
              <input className="mobile-input" placeholder="Your name" value={createForm.completed_by} onChange={e => setCreateForm(f => ({ ...f, completed_by: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="mobile-action-btn" style={{ background: '#f2f2f7', color: '#636366' }} onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="mobile-action-btn primary" onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating...' : 'Create Receipt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
